import {
  CADENCE_BOUNDS, NEW_CADENCE, PHASE_NAME_RE, strategiesFor, waitsFor,
  type CadenceFormValues, type PhaseValues,
} from "./cadence.js";

/**
 * The cadence builder's arithmetic (ADR-066/067): what a lead who never picks up actually gets
 * from a cadence, the templates a new one starts from, the words a wait reads as, and the edits
 * the builder makes to a phase. Pure, for `test/cadence-plan.test.ts`.
 *
 * `planCalls` plays the cadence forward the way the dialer runs it (`services/dialer/src/cadence.py`
 * in `platform`): a counted call moves the lead to the next call of its cycle after the wait
 * between calls, or to the next cycle after the longer wait; the cycle's shape is the phase's at
 * the moment the call ends; a mid-cycle call that could not go out before its deadline is dropped
 * and the lead starts the next cycle instead; nothing goes out outside the calling hours or past
 * the day's cap on one number; the cadence stops at its total, its expiry or its last phase. It
 * assumes nobody ever answers, the campaign's default calling hours, and no other campaign
 * calling the same number: a preview for reading a cadence, not a promise about a lead.
 */

const DAY = 24 * 60;

export interface PlanOptions {
  /** Minutes after midnight the lead joins, on their clock. */
  joinMinute?: number;
  /** The campaign's calling hours on the lead's clock, minutes after midnight. */
  windowStart?: number;
  windowEnd?: number;
  /** How long an unanswered call rings before it ends, in minutes. */
  ringMinutes?: number;
  /** Never play further than this. */
  horizonDays?: number;
}

export const PLAN_DEFAULTS = {
  joinMinute: 9 * 60,
  windowStart: 8 * 60,
  windowEnd: 21 * 60,
  ringMinutes: 0.5,
  horizonDays: 365,
} as const;

export type Moved = "quiet_hours" | "daily_cap" | "replanned";

export interface PlannedCall {
  /** Minutes since midnight of the day the lead joined. */
  at: number;
  /** 1 on the day the lead joined. */
  day: number;
  /** Minutes after midnight on that day. */
  minute: number;
  phase: number;
  cycle: number;
  /** The call of its cycle, from 0. */
  step: number;
  strategy: string;
  /** Why it went out later than it was due, if it did. */
  moved: Moved | null;
}

export type PlanEnd = "total_attempts" | "expired" | "phases_over" | "horizon";

export interface PlannedPhase {
  name: string;
  fromDay: number;
  /** Null for an open-ended last phase. */
  toDay: number | null;
  calls: number;
}

export interface Plan {
  calls: PlannedCall[];
  /** Calls on each day, from the day the lead joined through `lastDay`. */
  perDay: number[];
  /** The last day the plan covers: the last call's day, or the day the cadence ends. */
  lastDay: number;
  endedBy: PlanEnd;
  phases: PlannedPhase[];
}

const whole = (n: unknown, fallback: number, [min, max]: readonly [number, number]) =>
  typeof n === "number" && Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : fallback;

const days = (text: string): number | null => {
  const t = text.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};

export const dayOf = (at: number): number => Math.floor(at / DAY) + 1;

/** What a lead who never picks up gets, call by call. */
export function planCalls(v: CadenceFormValues, options: PlanOptions = {}): Plan {
  const o = { ...PLAN_DEFAULTS, ...options };
  const join = o.joinMinute;
  const total = whole(v.total_attempts, 1, CADENCE_BOUNDS.total_attempts);
  const cap = days(v.daily_calls_per_number);
  const phaseEnds = v.phases.map((p) => {
    const d = days(p.ends_days);
    return d === null ? null : join + d * DAY;
  });
  const expire = days(v.expire_days);
  const lastEnd = phaseEnds.length ? (phaseEnds[phaseEnds.length - 1] ?? null) : join;
  const candidates: [number, PlanEnd][] = [[join + o.horizonDays * DAY, "horizon"]];
  if (expire !== null) candidates.push([join + expire * DAY, "expired"]);
  if (lastEnd !== null) candidates.push([lastEnd, "phases_over"]);
  const [ends, endsBy] = candidates.reduce((a, b) => (b[0] < a[0] ? b : a));

  const phaseAt = (t: number): number | null => {
    for (let i = 0; i < phaseEnds.length; i++) {
      const end = phaseEnds[i];
      if (end === null || end === undefined || t < end) return i;
    }
    return null;
  };
  const shape = (i: number) => {
    const p = v.phases[i]!;
    const attempts = whole(p.attempts, 1, CADENCE_BOUNDS.attempts);
    return {
      attempts,
      waits: waitsFor(attempts, p.between_minutes).map((m) => whole(m, 1, CADENCE_BOUNDS.between_minutes)),
      strategies: strategiesFor(attempts, p.did_strategy),
    };
  };
  const inHours = (t: number) => {
    const m = ((t % DAY) + DAY) % DAY;
    return m >= o.windowStart && m < o.windowEnd;
  };
  const nextOpen = (t: number) => {
    const start = Math.floor(t / DAY) * DAY;
    const m = t - start;
    return m < o.windowStart ? start + o.windowStart : start + DAY + o.windowStart;
  };

  const calls: PlannedCall[] = [];
  const count = new Map<number, number>();
  let cycle = 1;
  let step = 0;
  let due = join + whole(v.phases[0]?.initial_delay_minutes, 0, CADENCE_BOUNDS.initial_delay_minutes);
  let notAfter: number | null = null;
  let endedBy: PlanEnd = endsBy;
  let endAt = ends;

  for (let guard = 0; guard < 1000 && v.phases.length > 0; guard++) {
    // The gate: the calling hours, then the day's cap, the way the claim reads them.
    let at = due;
    let moved: Moved | null = null;
    for (let g = 0; g < 1000; g++) {
      if (!inHours(at)) {
        at = nextOpen(at);
        moved ??= "quiet_hours";
        continue;
      }
      if (cap !== null && (count.get(dayOf(at)) ?? 0) >= cap) {
        at = Math.floor(at / DAY) * DAY + DAY + o.windowStart;
        moved = "daily_cap";
        continue;
      }
      break;
    }
    // A mid-cycle call past its deadline is not made late: the next cycle starts instead.
    if (step > 0 && notAfter !== null && at > notAfter) {
      cycle += 1;
      step = 0;
      notAfter = null;
      moved = "replanned";
    }
    if (at >= ends) {
      endAt = ends;
      break;
    }
    const phase = phaseAt(at);
    if (phase === null) {
      endedBy = "phases_over";
      endAt = at;
      break;
    }
    const s = shape(phase);
    calls.push({
      at,
      day: dayOf(at),
      minute: at - Math.floor(at / DAY) * DAY,
      phase,
      cycle,
      step,
      strategy: s.strategies[Math.min(step, s.strategies.length - 1)] ?? "branded",
      moved,
    });
    count.set(dayOf(at), (count.get(dayOf(at)) ?? 0) + 1);
    if (calls.length >= total) {
      endedBy = "total_attempts";
      endAt = at;
      break;
    }
    const ended = at + o.ringMinutes;
    if (ended >= ends) {
      endAt = ends;
      break;
    }
    const next = phaseAt(ended);
    if (next === null) {
      endedBy = "phases_over";
      endAt = ended;
      break;
    }
    const ns = shape(next);
    const p = v.phases[next]!;
    if (step + 1 < ns.attempts) {
      due = ended + (ns.waits[Math.min(step, ns.waits.length - 1)] ?? 1);
      step += 1;
      const late = days(p.not_after_minutes);
      notAfter = late === null ? null : due + late;
    } else {
      due = ended + whole(p.after_cycle_minutes, DAY, CADENCE_BOUNDS.wait_minutes);
      cycle += 1;
      step = 0;
      notAfter = null;
    }
  }

  const lastCall = calls[calls.length - 1];
  const lastDay = endedBy === "total_attempts" && lastCall ? lastCall.day : dayOf(Math.max(join, endAt - 1));
  const perDay = Array.from({ length: Math.max(1, lastDay) }, (_, i) => count.get(i + 1) ?? 0);
  let from = 1;
  const phases = v.phases.map((p, i) => {
    const to = days(p.ends_days);
    const out: PlannedPhase = { name: p.name, fromDay: from, toDay: to, calls: calls.filter((c) => c.phase === i).length };
    from = (to ?? from) + 1;
    return out;
  });
  return { calls, perDay, lastDay, endedBy, phases };
}

// --- what the preview says -------------------------------------------------------------------------

export interface PlanNote {
  tone: "info" | "warning";
  text: string;
}

/** A phase as the admin reads it: "“followup” (days 15–90)". */
export function phaseRange(p: { name: string; fromDay: number; toDay: number | null }): string {
  const days = p.toDay === null ? `day ${p.fromDay} on` : p.fromDay === p.toDay ? `day ${p.fromDay}` : `days ${p.fromDay}–${p.toDay}`;
  return `“${p.name}” (${days})`;
}

/** What is worth knowing about a cadence that its numbers alone do not say. */
export function planNotes(v: CadenceFormValues, plan: Plan, uncapped?: Plan): PlanNote[] {
  const out: PlanNote[] = [];
  const total = plan.calls.length;
  if (plan.endedBy === "total_attempts") {
    const unreached = plan.phases.filter((p, i) => i > 0 && p.calls === 0);
    if (unreached.length) {
      out.push({
        tone: "warning",
        text: `All ${total} calls are made by day ${plan.lastDay}, so ${unreached.map(phaseRange).join(" and ")} never ${unreached.length === 1 ? "gets" : "get"} a call. Allow more calls in all, or space the earlier calls out.`,
      });
    }
  }
  const cap = days(v.daily_calls_per_number);
  if (cap !== null && uncapped) {
    const firstWeek = uncapped.perDay.slice(0, 7);
    const most = Math.max(0, ...firstWeek);
    if (most > cap) {
      out.push({
        tone: "info",
        text: `The daily cap holds the busiest days to ${cap} call${cap === 1 ? "" : "s"}; without it the first week would reach ${most} a day.`,
      });
    }
  }
  const expire = days(v.expire_days);
  const last = plan.phases[plan.phases.length - 1];
  if (expire !== null && last && (last.toDay === null || last.toDay > expire) && plan.endedBy !== "total_attempts") {
    const cut = plan.phases.filter((p) => p.fromDay > expire);
    if (cut.length) {
      out.push({
        tone: "warning",
        text: `The cadence stops on day ${expire}, before ${cut.map(phaseRange).join(" and ")} ${cut.length === 1 ? "starts" : "start"}.`,
      });
    } else if (last.toDay !== null) {
      out.push({ tone: "info", text: `The cadence stops on day ${expire}, before ${phaseRange(last)} is over.` });
    }
  }
  if (expire === null && last?.toDay === null && plan.endedBy === "total_attempts") {
    out.push({ tone: "info", text: `No end day: a lead is called until all ${total} calls are made.` });
  }
  const replanned = plan.calls.filter((c) => c.moved === "replanned").length;
  if (replanned > 0) {
    out.push({
      tone: "info",
      text: `${replanned} time${replanned === 1 ? "" : "s"} a second call runs past its deadline (calling hours or the daily cap) and the lead starts the next cycle instead.`,
    });
  }
  return out;
}

/** The busiest day's calls, the day it happens first. */
export function busiestDay(plan: Plan): { day: number; calls: number } {
  let best = { day: 1, calls: 0 };
  plan.perDay.forEach((n, i) => {
    if (n > best.calls) best = { day: i + 1, calls: n };
  });
  return best;
}

// --- words --------------------------------------------------------------------------------------------

/** "right away", "1 min", "90 min", "4 hours", "1 day", "3 days", "1 week", "1 h 30 min". */
export function waitWords(minutes: number): string {
  if (!Number.isFinite(minutes)) return "—";
  if (minutes <= 0) return "right away";
  if (minutes % (7 * DAY) === 0) {
    const w = minutes / (7 * DAY);
    return `${w} week${w === 1 ? "" : "s"}`;
  }
  if (minutes % DAY === 0) {
    const d = minutes / DAY;
    return `${d} day${d === 1 ? "" : "s"}`;
  }
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  if (minutes < 120) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** "9:00 am", "1:01 pm", "noon". */
export function clockWords(minute: number): string {
  const m = Math.floor(((minute % DAY) + DAY) % DAY);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 12 && mm === 0) return "noon";
  const suffix = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${suffix}`;
}

/** The day a phase starts on, counted from 1 on the day the lead joined. */
export function phaseFromDay(values: CadenceFormValues, index: number): number {
  let from = 1;
  for (let i = 0; i < index; i++) from = (days(values.phases[i]?.ends_days ?? "") ?? from - 1) + 1;
  return from;
}

/** The days a phase covers, for its card: "Days 1–14", "Days 15–90", "From day 91". */
export function phaseDays(values: CadenceFormValues, index: number): string {
  const from = phaseFromDay(values, index);
  const to = days(values.phases[index]?.ends_days ?? "");
  if (to === null) return from === 1 ? "Every day" : `From day ${from}`;
  return from === to ? `Day ${from}` : `Days ${from}–${to}`;
}

// --- presets ------------------------------------------------------------------------------------------

/** The waits a picker offers, in minutes, by what the wait is for. */
export const WAIT_PRESETS = {
  between: [0, 1, 2, 5, 10, 15, 30, 60, 120],
  cycle: [60, 120, 240, 360, 480, DAY, 2 * DAY, 3 * DAY, 7 * DAY],
  firstCall: [0, 1, 5, 15, 30, 60, 240, DAY],
  lateCall: [30, 60, 120, 240, 480, DAY],
  technical: [1, 2, 5, 10, 15, 30, 60],
  notWorked: [5, 10, 15, 30, 60, 120],
  callback: [15, 30, 60, 120, 240, DAY],
} as const;

export const DAY_PRESETS = [3, 7, 10, 14, 21, 30, 45, 60, 90, 120, 180, 365] as const;

/** The days a phase may end on: after the phase before it, before the phase after it. */
export function phaseEndBounds(values: CadenceFormValues, index: number): { min: number; max: number; open: boolean } {
  const before = index > 0 ? days(values.phases[index - 1]?.ends_days ?? "") ?? 0 : 0;
  const after = index < values.phases.length - 1 ? days(values.phases[index + 1]?.ends_days ?? "") : null;
  return {
    min: before + 1,
    max: after === null ? CADENCE_BOUNDS.expire_days[1] : after - 1,
    open: index === values.phases.length - 1,
  };
}

// --- edits --------------------------------------------------------------------------------------------

/** A new phase after the last. An open-ended last phase gets an end two weeks on, so only the new
 *  one is open-ended; otherwise the new one runs a month past the last. */
export function withPhase(values: CadenceFormValues): CadenceFormValues {
  if (values.phases.length >= CADENCE_BOUNDS.phases[1]) return values;
  const phases = values.phases.map((p) => ({ ...p }));
  const last = phases[phases.length - 1];
  const taken = new Set(phases.map((p) => p.name));
  let n = phases.length + 1;
  let name = `phase_${n}`;
  while (taken.has(name)) name = `phase_${++n}`;
  let ends = "";
  if (last) {
    const lastEnd = days(last.ends_days);
    if (lastEnd === null) {
      const start = phases.length > 1 ? (days(phases[phases.length - 2]!.ends_days) ?? 0) : 0;
      last.ends_days = String(Math.min(CADENCE_BOUNDS.expire_days[1] - 1, start + 14));
    } else {
      ends = String(Math.min(CADENCE_BOUNDS.expire_days[1], lastEnd + 30));
    }
  }
  phases.push({
    name, ends_days: ends, priority_band: 100, age_decay_per_day: 0, not_after_minutes: "",
    attempts: 1, between_minutes: [], after_cycle_minutes: 3 * DAY, initial_delay_minutes: 0,
    did_strategy: ["branded"],
  });
  return { ...values, phases };
}

/** The cadence without phase `index`; a cadence keeps one phase. */
export function withoutPhase(values: CadenceFormValues, index: number): CadenceFormValues {
  if (values.phases.length <= 1) return values;
  return { ...values, phases: values.phases.filter((_, i) => i !== index) };
}

/** Whether a phase name is one the server takes, and unique in the cadence. */
export function phaseNameProblem(values: CadenceFormValues, index: number, name: string): string | null {
  const n = name.trim();
  if (!PHASE_NAME_RE.test(n)) return "Lowercase letters, digits and underscores, starting with a letter, up to 24";
  if (values.phases.some((p, i) => i !== index && p.name === n)) return "Another phase has this name";
  return null;
}

/** A slug from a name: lowercase words joined by underscores, starting with a letter, unique
 *  among `taken`. */
export function slugFrom(name: string, taken: Iterable<string> = []): string {
  let base = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (base && !/^[a-z]/.test(base)) base = `c_${base}`;
  base = base.slice(0, 72).replace(/_+$/g, "");
  if (base.length < 2) base = "cadence";
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let i = 2; ; i++) {
    const next = `${base}_${i}`;
    if (!used.has(next)) return next;
  }
}

// --- templates ----------------------------------------------------------------------------------------

export interface CadenceTemplate {
  id: string;
  name: string;
  description: string;
  values: CadenceFormValues;
}

const phase = (p: Partial<PhaseValues> & Pick<PhaseValues, "name">): PhaseValues => ({
  ends_days: "", priority_band: 400, age_decay_per_day: 0, not_after_minutes: "", attempts: 1,
  between_minutes: [], after_cycle_minutes: 240, initial_delay_minutes: 0, did_strategy: ["branded"],
  ...p,
});

const base = (over: Partial<CadenceFormValues>): CadenceFormValues => ({
  ...NEW_CADENCE,
  phases: NEW_CADENCE.phases.map((p) => ({ ...p, between_minutes: [...p.between_minutes], did_strategy: [...p.did_strategy] })),
  ...over,
});

/** Where a new cadence starts. Every number is the admin's to change; these are shapes the floor
 *  already talks about. */
export const TEMPLATES: readonly CadenceTemplate[] = [
  {
    id: "speed_to_lead",
    name: "Speed to lead",
    description: "Two calls a minute apart, again every 4 hours for two weeks, then one call every 3 days.",
    values: base({ name: "Speed to lead" }),
  },
  {
    id: "triple_dial",
    name: "Triple dial",
    description: "Three calls back to back, the third from a local number, every 4 hours for a week; then one a day.",
    values: base({
      name: "Triple dial",
      phases: [
        phase({
          name: "first_week", ends_days: "7", priority_band: 400, age_decay_per_day: 10, not_after_minutes: "120",
          attempts: 3, between_minutes: [0, 0], after_cycle_minutes: 240,
          did_strategy: ["branded", "same_as_last", "unbranded"],
        }),
        phase({ name: "daily", ends_days: "30", priority_band: 100, after_cycle_minutes: DAY }),
      ],
      total_attempts: 30,
      expire_days: "30",
      daily_calls_per_number: "3",
    }),
  },
  {
    id: "steady",
    name: "Every few hours",
    description: "One call every 4 hours for a week, then one a day for three weeks.",
    values: base({
      name: "Every few hours",
      phases: [
        phase({ name: "first_week", ends_days: "7", priority_band: 400, age_decay_per_day: 10, after_cycle_minutes: 240 }),
        phase({ name: "daily", ends_days: "30", priority_band: 100, after_cycle_minutes: DAY }),
      ],
      total_attempts: 30,
      expire_days: "30",
      daily_calls_per_number: "3",
    }),
  },
  {
    id: "gentle",
    name: "Once a day",
    description: "One call a day for two weeks, then one a week for six weeks.",
    values: base({
      name: "Once a day",
      phases: [
        phase({ name: "daily", ends_days: "14", priority_band: 300, age_decay_per_day: 5, after_cycle_minutes: DAY }),
        phase({ name: "weekly", ends_days: "56", priority_band: 100, after_cycle_minutes: 7 * DAY }),
      ],
      total_attempts: 20,
      expire_days: "56",
      daily_calls_per_number: "2",
    }),
  },
];

/** A deep copy of a template's values, so edits never reach the shared constant. */
export function fromTemplate(t: CadenceTemplate): CadenceFormValues {
  return {
    ...t.values,
    phases: t.values.phases.map((p) => ({ ...p, between_minutes: [...p.between_minutes], did_strategy: [...p.did_strategy] })),
  };
}
